import { useEffect, useId, useRef, useState } from 'react';
import { LoaderCircle, Paperclip, Send, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { borderGrey, cardBg, greenButtonClass, primaryText, redTextColor, secondaryText } from '../../../styling/stylingHelper';

export interface SupportDraft { body: string; files: File[]; clientMessageId: string }
export function validateSupportFiles(files: File[]): boolean {
  return files.length <= 3 && files.every(file => ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'].includes(file.type) && file.size <= 5 * 1024 * 1024)
    && files.reduce((sum, file) => sum + file.size, 0) <= 15 * 1024 * 1024;
}

export function SupportComposer({ onSend, onDraftChange, onSendingChange, bug = false, creating = false, disabled = false }: {
  onSend: (draft: SupportDraft) => Promise<void>;
  onDraftChange?: (dirty: boolean) => void;
  onSendingChange?: (sending: boolean) => void;
  bug?: boolean;
  creating?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation('support');
  const id = useId();
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(crypto.randomUUID());
  const mounted = useRef(true);
  const draftChanged = useRef(onDraftChange);
  const sendingChanged = useRef(onSendingChange);
  draftChanged.current = onDraftChange;
  sendingChanged.current = onSendingChange;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; draftChanged.current?.(false); sendingChanged.current?.(false); }; }, []);
  useEffect(() => { draftChanged.current?.(body.length > 0 || files.length > 0 || sending); }, [body, files, sending]);
  useEffect(() => { sendingChanged.current?.(sending); }, [sending]);
  const edited = () => { requestId.current = crypto.randomUUID(); setError(null); };
  const send = async () => {
    if (sending || disabled || !body.trim() || body.trim().length > 10_000) return;
    setSending(true); setError(null);
    try {
      await onSend({ body: body.trim(), files, clientMessageId: requestId.current });
      if (mounted.current) { setBody(''); setFiles([]); requestId.current = crypto.randomUUID(); }
    } catch {
      if (mounted.current) setError('sendFailed');
    } finally { if (mounted.current) setSending(false); }
  };
  return <form className={creating ? 'space-y-4' : 'space-y-2'} onSubmit={event => { event.preventDefault(); void send(); }}>
    <label htmlFor={`${id}-body`} className={cn(creating ? 'block font-medium' : 'sr-only', primaryText)}>{t(creating ? 'messageLabel' : 'replyLabel')}</label>
    {bug && <p id={`${id}-help`} className={cn('text-sm', secondaryText)}>{t('bugHelp')}</p>}
    <textarea id={`${id}-body`} aria-describedby={bug ? `${id}-help` : undefined} required maxLength={10000} value={body}
      onChange={event => { edited(); setBody(event.target.value); }} disabled={sending || disabled} rows={creating ? 7 : 2}
      placeholder={t(creating ? 'messagePlaceholder' : 'replyPlaceholder')}
      className={cn('block w-full resize-y rounded-xl border p-3 disabled:opacity-60', !creating && 'min-h-[64px] max-h-32 text-sm', borderGrey, cardBg, primaryText)} />
    <div className={cn('flex items-center justify-between gap-2', creating && 'flex-wrap')}>
      <label htmlFor={`${id}-files`} title={t('attach')} className={cn('flex min-h-[44px] shrink-0 cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 focus-within:ring-2 focus-within:ring-emerald-600 dark:focus-within:ring-emerald-400', creating && 'border-dashed', borderGrey, secondaryText)}>
        <Paperclip size={18} aria-hidden="true" /><span className={!creating ? 'sr-only' : undefined}>{t('attach')}</span>
        <input id={`${id}-files`} type="file" multiple accept="image/png,image/jpeg,image/webp,application/pdf" disabled={sending || disabled}
          className="sr-only" onChange={event => {
            const next = [...files, ...Array.from(event.target.files ?? [])]; event.target.value = '';
            if (!validateSupportFiles(next)) { setError('filesInvalid'); return; }
            edited(); setFiles(next);
          }} />
      </label>
      <span className={cn('text-xs', !creating && 'min-w-0 flex-1', secondaryText)}>{t('fileLimits')}</span>
      {!creating && <button type="submit" disabled={sending || disabled || !body.trim()} title={t(sending ? 'sending' : error === 'sendFailed' ? 'retrySend' : 'sendReply')}
        className={cn(greenButtonClass, 'min-h-[44px] min-w-[44px] shrink-0 justify-center px-3 text-sm disabled:opacity-50 sm:px-4')}>
        {sending ? <LoaderCircle size={18} aria-hidden="true" className="animate-spin sm:hidden" /> : <Send size={18} aria-hidden="true" className="sm:hidden" />}
        <span className="sr-only sm:not-sr-only">{t(sending ? 'sending' : error === 'sendFailed' ? 'retrySend' : 'sendReply')}</span>
      </button>}
    </div>
    {files.length > 0 && <ul className="space-y-2">{files.map((file, index) => <li key={`${file.name}-${index}`} className={cn('flex items-center gap-2 rounded-lg border p-2 text-sm', borderGrey)}>
      <span className="min-w-0 flex-1 break-all">{file.name} · {(file.size / 1024).toFixed(0)} {t('kilobytes')}</span>
      <button type="button" disabled={sending} className="flex h-11 w-11 shrink-0 items-center justify-center" aria-label={t('removeFile', { name: file.name })} onClick={() => { edited(); setFiles(files.filter((_, i) => i !== index)); }}><X size={18} /></button>
    </li>)}</ul>}
    {error && <p role="alert" className={cn('text-sm', redTextColor)}>{t(error)}</p>}
    {creating && <button type="submit" disabled={sending || disabled || !body.trim()} className={cn(greenButtonClass, 'min-h-[44px] justify-center gap-2 disabled:opacity-50')}>
      {t(sending ? 'sending' : 'submit')}
    </button>}
  </form>;
}
